<template>
    <div class="water-account-lookup">
        <!-- Header -->
        <div class="lookup-header">
            <div class="header-icon">
                <i class="bi bi-search"></i>
            </div>
            <div>
                <h3 class="lookup-title">Link Water Account</h3>
                <p class="lookup-subtitle">Search and link an existing water utility account</p>
            </div>
        </div>

        <!-- Search Form -->
        <div class="search-form">
            <div class="input-group">
                <div class="input-icon">
                    <i class="bi bi-droplet"></i>
                </div>
                <input ref="actNumRef" type="text" v-model="accountNumber"
                    placeholder="Enter water account number (e.g., X-XXXX-X)" class="account-input"
                    :disabled="isLoading" @keyup.enter="searchAccount" />
                <button @click="searchAccount" class="btn-search" :disabled="isLoading || !accountNumber.trim()">
                    <i class="bi bi-search" v-if="!isLoading"></i>
                    <i class="bi bi-hourglass-split" v-else></i>
                    {{ isLoading ? 'Searching...' : 'Search' }}
                </button>
            </div>

            <!-- Validation Error -->
            <div v-if="validationError" class="validation-error">
                <i class="bi bi-exclamation-circle"></i>
                {{ validationError }}
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="loading-state">
            <div class="spinner">
                <i class="bi bi-arrow-repeat"></i>
            </div>
            <p>Searching for water account...</p>
        </div>

        <!-- No Results Found -->
        <div v-else-if="searchPerformed && !foundAccount" class="no-results">
            <div class="no-results-icon">
                <i class="bi bi-search"></i>
            </div>
            <h4>Account Not Found</h4>
            <p>No water account found with number: <strong>{{ accountNumber }}</strong></p>
            <div class="suggestions">
                <p>Please check:</p>
                <ul>
                    <li>Account number format (e.g., X-XXXX-X)</li>
                    <li>Spacing and special characters</li>
                    <li>Contact your water utility provider</li>
                </ul>
            </div>
            <button @click="resetSearch" class="btn-try-again">
                <i class="bi bi-arrow-clockwise"></i>
                Try Another Account
            </button>
        </div>

        <!-- Account Found -->
        <div v-else-if="foundAccount" class="account-found">
            <div class="success-header">
                <div class="success-icon">
                    <i class="bi bi-check-circle"></i>
                </div>
                <div>
                    <h4>Account Found!</h4>
                    <p>Water account verified successfully</p>
                </div>
            </div>

            <!-- Account Details Card -->
            <div class="account-details-card">
                <div class="account-details-header">
                    <div class="account-number-display">
                        <span class="label">Account Number:</span>
                        <span class="value">{{ foundAccount.account }}</span>
                    </div>
                    <div class="verification-badge">
                        <i class="bi bi-shield-check"></i>
                        Verified
                    </div>
                </div>

                <div class="account-info-grid">
                    <div class="info-item">
                        <div class="info-label">
                            <i class="bi bi-person"></i>
                            Account Holder
                        </div>
                        <div class="info-value">{{ foundAccount.account_name }}</div>
                    </div>

                    <div class="info-item">
                        <div class="info-label">
                            <i class="bi bi-geo-alt"></i>
                            Service Address
                        </div>
                        <div class="info-value">{{ foundAccount.service_address }}</div>
                    </div>

                    <div class="info-item">
                        <div class="info-label">
                            <i class="bi bi-lightning-charge"></i>
                            Account Status
                        </div>
                        <div class="info-value">
                            <span :class="`status-${foundAccount.status.toLowerCase()}`">
                                {{ foundAccount.status }}
                            </span>
                        </div>
                    </div>

                    <div class="info-item">
                        <div class="info-label">
                            <i class="bi bi-calendar"></i>
                            Connection Date
                        </div>
                        <div class="info-value">{{ formatDate(foundAccount.connection_date) }}</div>
                    </div>
                </div>

                <!-- Current Balance -->
                <div class="balance-section">
                    <div class="balance-label">
                        <i class="bi bi-cash-stack"></i>
                        Current Bill
                    </div>
                    <div class="balance-amount">₱{{ formatCurrency(foundAccount.balance) }}</div>
                    <div v-if="foundAccount.current_balance > 0" class="balance-warning">
                        <i class="bi bi-exclamation-triangle"></i>
                        This account has an outstanding balance
                    </div>
                </div>

                <!-- Bind Actions -->
                <div class="bind-actions">
                    <div class="action-note">
                        <i class="bi bi-info-circle"></i>
                        Linking this account will allow you to view bills and make payments
                    </div>

                    <div class="action-buttons">
                        <button @click="bindAccount" class="btn-bind" :disabled="isBinding">
                            <i class="bi bi-link" v-if="!isBinding"></i>
                            <i class="bi bi-hourglass-split" v-else></i>
                            {{ isBinding ? 'Linking...' : 'Link to Business Account' }}
                        </button>

                        <button @click="resetSearch" class="btn-cancel">
                            <i class="bi bi-x-circle"></i>
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Instructions (Initial State) -->
        <div v-else class="instructions">
            <div class="instructions-content">
                <h4>How to find your account number:</h4>
                <div class="instruction-steps">
                    <div class="step">
                        <div class="step-number">1</div>
                        <div class="step-content">
                            <strong>Check your water bill</strong>
                            <p>Look for the account number on your physical or digital water bill</p>
                        </div>
                    </div>
                    <div class="step">
                        <div class="step-number">2</div>
                        <div class="step-content">
                            <strong>Contact water provider</strong>
                            <p>Call your local water utility company for assistance</p>
                        </div>
                    </div>
                    <div class="step">
                        <div class="step-number">3</div>
                        <div class="step-content">
                            <strong>Online account portal</strong>
                            <p>Check your online water account if available</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Success Modal -->
        <div v-if="showSuccessModal" class="success-modal-overlay">
            <div class="success-modal">
                <div class="modal-icon">
                    <i class="bi bi-check-circle"></i>
                </div>
                <h3>Account Linked Successfully!</h3>
                <p>Water account <strong>{{ foundAccount.account_number }}</strong> has been linked to your business.
                </p>
                <div class="modal-actions">
                    <button @click="closeSuccessModal" class="btn-modal-close">
                        <i class="bi bi-check"></i>
                        Continue
                    </button>
                    <button @click="$emit('view-accounts')" class="btn-modal-view">
                        <i class="bi bi-eye"></i>
                        View All Accounts
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import toastService from '@/services/toastService'
import washService from '@/services/washService'
import { ref, watch } from 'vue'
//eslint-disable-next-line
const props = defineProps({
    businessId: {
        type: [Number, String],
        required: true,
        default: 0
    }
})
//eslint-disable-next-line
const emit = defineEmits([
    'account-bound',
    'search-performed',
    'view-accounts'
])
const actNumRef = ref(null);

// Reactive state
const accountNumber = ref('')
const isLoading = ref(false)
const searchPerformed = ref(false)
const validationError = ref('')
const foundAccount = ref(null)
const isBinding = ref(false)
const showSuccessModal = ref(false)

// Watch for account number changes to clear validation
watch(accountNumber, () => {
    if (validationError.value) {
        validationError.value = ''
    }
    if (searchPerformed.value && !foundAccount.value) {
        searchPerformed.value = false
    }
})

// Methods
const validateAccountNumber = () => {
    const accNum = accountNumber.value.trim()

    if (!accNum) {
        validationError.value = 'Please enter an account number'
        return false
    }

    if (accNum.length < 5) {
        validationError.value = 'Account number is too short'
        return false
    }

    if (accNum.length > 50) {
        validationError.value = 'Account number is too long'
        return false
    }

    return true
}

const searchAccount = async () => {
    if (!validateAccountNumber()) {
        return
    }

    isLoading.value = true
    validationError.value = ''
    foundAccount.value = null
    searchPerformed.value = true
    foundAccount.value = await washService.lookup(accountNumber.value.trim());
    isLoading.value = false;
}

const bindAccount = async () => {
    if (!foundAccount.value) return

    isBinding.value = true

    try {
        const resp = await washService.bindAccount(foundAccount.value.account)
        if (resp.success) {
            // Emit event for parent component
            emit('account-bound', foundAccount.value)
            showSuccessModal.value = true;
        }
        else {
            validationError.value = resp.message;
            toastService.error(resp.message);
            actNumRef.value.focus();
        }

    } catch (error) {
        console.error('Binding error:', error)
        validationError.value = 'Failed to link account. Please try again.'
    } finally {
        isBinding.value = false
    }
}

const resetSearch = () => {
    accountNumber.value = ''
    isLoading.value = false
    searchPerformed.value = false
    validationError.value = ''
    foundAccount.value = null
    isBinding.value = false
    actNumRef.value?.focus();
}

const closeSuccessModal = () => {
    showSuccessModal.value = false
    resetSearch()
}

// Helper methods
const formatCurrency = (amount) => {
    if (!amount) return '0.00'
    return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })
}

const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return dateString

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        })
    } catch {
        return dateString
    }
}
//eslint-disable-next-line
defineExpose({
    resetSearch
})
</script>

<style scoped>
.water-account-lookup {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid #e9ecef;
    overflow: hidden;
}

/* Header */
.lookup-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem;
    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
    color: white;
}

.header-icon {
    font-size: 2rem;
    opacity: 0.9;
}

.lookup-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
}

.lookup-subtitle {
    margin: 0.25rem 0 0 0;
    opacity: 0.9;
    font-size: 0.875rem;
}

/* Search Form */
.search-form {
    padding: 1.5rem;
    border-bottom: 1px solid #e9ecef;
}

.input-group {
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

.input-icon {
    width: 50px;
    height: 50px;
    border-radius: 8px;
    background: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #dc3545;
    font-size: 1.25rem;
    border: 1px solid #dee2e6;
}

.account-input {
    flex: 1;
    padding: 0.75rem 1rem;
    border: 2px solid #dee2e6;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.3s ease;
}

.account-input:focus {
    outline: none;
    border-color: #dc3545;
    box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.account-input:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
}

.btn-search {
    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.3s ease;
    min-width: 120px;
    justify-content: center;
}

.btn-search:hover:not(:disabled) {
    background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
}

.btn-search:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #6c757d;
}

.validation-error {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #dc3545;
    font-size: 0.875rem;
    margin-top: 0.75rem;
    padding: 0.5rem;
    background: #fff5f5;
    border-radius: 6px;
    border: 1px solid #f8d7da;
}

/* Loading State */
.loading-state {
    text-align: center;
    padding: 3rem;
}

.spinner {
    font-size: 2.5rem;
    color: #dc3545;
    margin-bottom: 1rem;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.loading-state p {
    color: #6c757d;
}

/* No Results */
.no-results {
    text-align: center;
    padding: 2rem 1.5rem;
}

.no-results-icon {
    font-size: 3rem;
    color: #6c757d;
    opacity: 0.5;
    margin-bottom: 1rem;
}

.no-results h4 {
    color: #2c3e50;
    margin-bottom: 0.5rem;
}

.no-results p {
    color: #6c757d;
    margin-bottom: 1.5rem;
}

.suggestions {
    text-align: left;
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1.5rem;
    border: 1px solid #e9ecef;
}

.suggestions p {
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.suggestions ul {
    margin: 0;
    padding-left: 1.5rem;
    color: #6c757d;
}

.suggestions li {
    margin-bottom: 0.25rem;
}

.btn-try-again {
    background: transparent;
    color: #dc3545;
    border: 1px solid #dc3545;
    padding: 0.75rem 1.5rem;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.3s ease;
}

.btn-try-again:hover {
    background: #dc3545;
    color: white;
}

/* Account Found */
.account-found {
    padding: 1.5rem;
}

.success-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.success-icon {
    font-size: 2rem;
    color: #28a745;
}

.success-header h4 {
    margin: 0;
    color: #2c3e50;
}

.success-header p {
    margin: 0.25rem 0 0 0;
    color: #6c757d;
}

/* Account Details Card */
.account-details-card {
    background: #f8f9fa;
    border-radius: 10px;
    border: 1px solid #e9ecef;
    overflow: hidden;
}

.account-details-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    background: white;
    border-bottom: 1px solid #e9ecef;
}

.account-number-display {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.account-number-display .label {
    font-size: 0.75rem;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.account-number-display .value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #2c3e50;
}

.verification-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #28a745;
    background: #d4edda;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 600;
}

.account-info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
    padding: 1.5rem;
    background: white;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.info-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #6c757d;
    font-weight: 500;
}

.info-value {
    font-size: 1rem;
    color: #2c3e50;
    font-weight: 500;
}

.status-active {
    color: #28a745;
    font-weight: 600;
}

.status-inactive {
    color: #6c757d;
    font-weight: 600;
}

.status-suspended {
    color: #dc3545;
    font-weight: 600;
}

/* Balance Section */
.balance-section {
    padding: 1.5rem;
    background: white;
    border-top: 1px solid #e9ecef;
}

.balance-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1rem;
    color: #2c3e50;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.balance-amount {
    font-size: 2rem;
    font-weight: 700;
    color: #dc3545;
    margin-bottom: 0.5rem;
}

.balance-warning {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #dc3545;
    font-size: 0.875rem;
    background: #fff5f5;
    padding: 0.5rem;
    border-radius: 6px;
    border: 1px solid #f8d7da;
}

/* Bind Actions */
.bind-actions {
    padding: 1.5rem;
    background: #f8f9fa;
    border-top: 1px solid #e9ecef;
}

.action-note {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #6c757d;
    font-size: 0.875rem;
    margin-bottom: 1rem;
    padding: 0.75rem;
    background: white;
    border-radius: 6px;
    border: 1px dashed #dee2e6;
}

.action-buttons {
    display: flex;
    gap: 1rem;
}

.btn-bind {
    flex: 1;
    background: linear-gradient(135deg, #28a745 0%, #218838 100%);
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    justify-content: center;
    transition: all 0.3s ease;
}

.btn-bind:hover:not(:disabled) {
    background: linear-gradient(135deg, #218838 0%, #1e7e34 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.btn-bind:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #6c757d;
}

.btn-cancel {
    background: transparent;
    color: #6c757d;
    border: 1px solid #dee2e6;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.3s ease;
}

.btn-cancel:hover {
    background: #f8f9fa;
    border-color: #dc3545;
    color: #dc3545;
}

/* Instructions */
.instructions {
    padding: 1.5rem;
}

.instructions-content h4 {
    color: #2c3e50;
    margin-bottom: 1rem;
}

.instruction-steps {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.step {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

.step-number {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #dc3545;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    flex-shrink: 0;
}

.step-content {
    flex: 1;
}

.step-content strong {
    color: #2c3e50;
    display: block;
    margin-bottom: 0.25rem;
}

.step-content p {
    color: #6c757d;
    margin: 0;
    font-size: 0.875rem;
}

/* Success Modal */
.success-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.3s ease;
}

.success-modal {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    max-width: 400px;
    width: 90%;
    text-align: center;
    animation: slideUp 0.3s ease;
}

.modal-icon {
    font-size: 3rem;
    color: #28a745;
    margin-bottom: 1rem;
}

.success-modal h3 {
    color: #2c3e50;
    margin-bottom: 1rem;
}

.success-modal p {
    color: #6c757d;
    margin-bottom: 1.5rem;
}

.modal-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.btn-modal-close {
    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
    color: white;
    border: none;
    padding: 0.75rem;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: center;
    transition: all 0.3s ease;
}

.btn-modal-close:hover {
    background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
}

.btn-modal-view {
    background: transparent;
    color: #dc3545;
    border: 1px solid #dc3545;
    padding: 0.75rem;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: center;
    transition: all 0.3s ease;
}

.btn-modal-view:hover {
    background: #dc3545;
    color: white;
}

/* Animations */
@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .input-group {
        flex-direction: column;
    }

    .input-icon {
        display: none;
    }

    .account-input,
    .btn-search {
        width: 100%;
    }

    .account-info-grid {
        grid-template-columns: 1fr;
    }

    .action-buttons {
        flex-direction: column;
    }

    .success-modal {
        padding: 1.5rem;
        margin: 1rem;
    }

    .account-details-header {
        flex-direction: column;
        gap: 1rem;
        text-align: center;
    }
}
</style>